/**
 * cplog.ts — NEXXT PM26 CP-log integratie
 *
 * ⚠ PM26: nieuwe sheet + token vereist in prepared-platform
 * functions/src/triggers/cpLogAppend.ts (SPREADSHEET_ID + CP_TOKEN) — tot die
 * deploy geeft de functie 401 en blijft alles veilig in de offline-queue staan.
 *
 * Schrijft timeline-entries naar het officiële NEXXT CP-log (Google Sheet)
 * via de appendCpLog Cloud Function. Bij netwerkverlies worden entries
 * gebufferd in localStorage en automatisch opnieuw verzonden.
 */
import { useState, useEffect } from "react";

const ENDPOINT =
  "https://europe-west1-prepared-platform.cloudfunctions.net/appendCpLog";
const TOKEN = "pm26-cp-log-5183";
const QUEUE_KEY = "pm26.cplog.queue";
const LOGGED_KEY = "pm26.cplog.logged";

export interface CpLogEntry {
  tijd: string;
  melder: string;
  bericht: string;
  uitgezetNaar: string;
  afhandeling: string;
}

async function postEntry(entry: CpLogEntry): Promise<void> {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), 10_000);
  try {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: TOKEN, ...entry }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } finally {
    clearTimeout(tid);
  }
}

function readQueue(): CpLogEntry[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as CpLogEntry[];
  } catch {
    return [];
  }
}

function writeQueue(q: CpLogEntry[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    /* quota — negeer */
  }
}

/**
 * Stuur een log-entry naar de Cloud Function.
 * Gooit een fout bij netwerkverlies of 4xx/5xx — de entry is dan al
 * aan de offline-wachtrij toegevoegd.
 */
export async function sendCpLog(entry: CpLogEntry): Promise<void> {
  try {
    await postEntry(entry);
  } catch {
    const q = readQueue();
    q.push(entry);
    writeQueue(q);
    throw new Error("offline");
  }
}

/**
 * Probeer alle wachtrij-items opnieuw te verzenden.
 * Gelukte items worden uit de wachtrij verwijderd; mislukte blijven staan.
 */
export async function flushCpLogQueue(): Promise<void> {
  const q = readQueue();
  if (q.length === 0) return;
  const remaining: CpLogEntry[] = [];
  for (const item of q) {
    try {
      await postEntry(item);
    } catch {
      remaining.push(item);
    }
  }
  writeQueue(remaining);
}

// Flush bij module-load (bijv. na een herlaad terwijl er items in de wachtrij stonden)
void flushCpLogQueue();

// Flush elke 60 seconden als de wachtrij niet leeg is
setInterval(() => {
  if (readQueue().length > 0) void flushCpLogQueue();
}, 60_000);

/** Sla op dat een entry succesvol gelogd is, zodat de knop een vinkje houdt. */
export function markLogged(entryId: string): void {
  try {
    const raw = localStorage.getItem(LOGGED_KEY);
    const ids: string[] = raw ? (JSON.parse(raw) as string[]) : [];
    if (!ids.includes(entryId)) {
      ids.push(entryId);
      localStorage.setItem(LOGGED_KEY, JSON.stringify(ids));
    }
  } catch {
    /* quota — negeer */
  }
}

/** Geeft true als de entry al als gelogd gemarkeerd is in localStorage. */
export function isLogged(entryId: string): boolean {
  try {
    const raw = localStorage.getItem(LOGGED_KEY);
    if (!raw) return false;
    return (JSON.parse(raw) as string[]).includes(entryId);
  } catch {
    return false;
  }
}

/**
 * Lichte hook die het aantal items in de offline-wachtrij bijhoudt.
 * Handig voor een badge in de TopBar of elders.
 */
export function useCpLogQueueCount(): number {
  const [count, setCount] = useState(() => readQueue().length);

  useEffect(() => {
    const id = setInterval(() => setCount(readQueue().length), 5_000);
    return () => clearInterval(id);
  }, []);

  return count;
}
